/**
 * Descriptors dos containers: tela, abas, aba, painel, linha e seção.
 *
 * A hierarquia que o `BuilderValidationService` impunha por código
 * (Tab -> Panel -> campo) aparece aqui como dado, em `container.accepts` e
 * `allowedParents`. A diferença prática: a validação passa a ser PREVENTIVA —
 * o arraste nem realça o alvo inválido — em vez de punitiva, que era mostrar um
 * aviso depois do clique e mesmo assim adicionar o componente.
 */
import { ScreenNode } from '../../model/node-props';
import { ComponentDescriptor, defineDescriptor } from '../component-descriptor';

/** Tipos que podem ficar dentro de um painel ou de uma linha. */
const FIELD_TYPES = [
  'text', 'textarea', 'number', 'date', 'switch',
  'select', 'dropdown', 'querySelect', 'search', 'tag',
  'keyValue', 'keyValueObject',
  'divider', 'image', 'button',
  'simpletable', 'table', 'datagrid', 'spreadsheet'
] as const;

// ---------------------------------------------------------------- tela

export const screenDescriptor = defineDescriptor<'screen'>({
  type: 'screen',
  labelKey: 'builderScreen.componentTypes.screen',
  icon: 'layout',
  category: 'layout',
  level: 'advanced',

  createDefault: () => ({
    props: {},
    layout: { order: 0 },
    children: []
  }),

  container: {
    layoutMode: 'stack',
    /**
     * A raiz aceita um container de abas OU painéis direto.
     *
     * Painel direto é o que permite **tela sem abas** — o modelo antigo forçava
     * Tab -> Panel -> campo sempre, e era por isso que toda tela gerada saía
     * parecendo cadastro com abas, mesmo quando não precisava.
     *
     * Sem `maxChildren`: uma tela sem abas costuma ter vários painéis na raiz.
     * O caso estranho (dois containers de abas na mesma tela) fica para a
     * verificação da tela apontar, não para a regra de arraste proibir.
     */
    accepts: ['tabs', 'panel']
  },

  allowedParents: [],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'titleKey',
          labelKey: 'builderScreen.inspector.title',
          level: 'simple'
        }
      ]
    }
  ]
});

// ---------------------------------------------------------------- abas

export const tabsDescriptor = defineDescriptor<'tabs'>({
  type: 'tabs',
  labelKey: 'builderScreen.palette.tabs',
  descriptionKey: 'builderScreen.palette.tabs.desc',
  icon: 'folder',
  category: 'layout',
  level: 'simple',

  createDefault: context => ({
    props: {},
    layout: { order: context.order },
    // Nasce com uma aba E um painel dentro dela.
    //
    // A aba vazia era pior que inútil: com uma aba só a barra não aparece (é o
    // certo para a tela publicada), e o conteúdo da única aba é o que se
    // desenha — nada. Quem acrescentava "Abas" via o canvas continuar igual.
    //
    // O painel também resolve o passo seguinte: a aba só aceita painel, então
    // sem ele o primeiro campo seria recusado.
    children: [
      {
        id: `${context.id}-tab-1`,
        type: 'tab',
        props: { labelKey: 'builderScreen.tab.main' },
        layout: { order: 0 },
        children: [
          {
            id: `${context.id}-tab-1-panel`,
            type: 'panel',
            props: { nameKey: 'builderScreen.panel.main' },
            layout: { order: 0 },
            children: []
          } as ScreenNode
        ]
      } as ScreenNode
    ]
  }),

  container: {
    layoutMode: 'tabs',
    accepts: ['tab'],
    minChildren: 1
  },

  // Tambem dentro de um painel — ver o comentario em `panelDescriptor`.
  allowedParents: ['screen', 'panel'],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.contextBarSection',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'custom',
          path: 'contextFields',
          editor: 'contextFields',
          labelKey: 'builderScreen.inspector.contextBarSection',
          level: 'advanced'
        }
      ]
    }
  ]
});

// ---------------------------------------------------------------- aba

export const tabDescriptor = defineDescriptor<'tab'>({
  type: 'tab',
  labelKey: 'builderScreen.componentTypes.tab',
  icon: 'file',
  category: 'layout',
  level: 'simple',

  createDefault: context => ({
    props: { labelKey: 'builderScreen.tab.general' },
    layout: { order: context.order },
    children: []
  }),

  container: {
    // Grade, e nao pilha: a aba passou a receber campo direto, e campo se
    // organiza na grade de 24 colunas como em qualquer outro container.
    layoutMode: 'grid',
    // O painel continua valendo — e o que o gerador produz quando ha muitos
    // campos —, mas deixou de ser OBRIGATORIO. Exigir um painel para pôr um
    // campo numa aba e a mesma friccao que fazia toda tela sair parecendo o
    // mesmo cadastro.
    accepts: ['panel', 'row', 'section', ...FIELD_TYPES]
  },

  allowedParents: ['tabs'],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.tabs.label',
          level: 'simple'
        },
        {
          kind: 'custom',
          path: 'icon',
          editor: 'iconPicker',
          labelKey: 'builderScreen.tabs.icon.title',
          helpKey: 'builderScreen.tabs.icon.help',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'disabled',
          labelKey: 'builderScreen.inspector.disabled',
          level: 'advanced'
        }
      ]
    }
  ]
});

// ---------------------------------------------------------------- painel

export const panelDescriptor = defineDescriptor<'panel'>({
  type: 'panel',
  labelKey: 'builderScreen.palette.panel',
  descriptionKey: 'builderScreen.palette.panel.desc',
  icon: 'block',
  category: 'layout',
  level: 'simple',

  createDefault: context => ({
    props: {
      nameKey: 'builderScreen.panel.main',
      active: true,
      collapsible: false,
      collapsed: false
    },
    layout: { order: context.order, colSpan: 24 },
    children: []
  }),

  container: {
    layoutMode: 'grid',
    // `tabs` na lista: sub-abas dentro de um painel sao um arranjo comum em tela
    // de negocio — um painel de dados e, dentro dele, abas para os detalhes.
    // A hierarquia de saida nao muda (abas -> aba -> painel -> campo); muda so
    // onde o container de abas pode comecar.
    accepts: ['row', 'section', 'tabs', ...FIELD_TYPES]
  },

  allowedParents: ['tab', 'screen'],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'nameKey',
          labelKey: 'builderScreen.panel.properties.name',
          placeholderKey: 'builderScreen.panel.properties.name.placeholder',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'collapsible',
          labelKey: 'builderScreen.inspector.collapsible',
          helpKey: 'builderScreen.inspector.collapsible.help',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'collapsed',
          labelKey: 'builderScreen.inspector.collapsed',
          helpKey: 'builderScreen.inspector.collapsed.help',
          level: 'simple',
          // Só faz sentido se o painel puder ser recolhido.
          visibleWhen: (node: ScreenNode) =>
            (node.props as { collapsible?: boolean }).collapsible === true
        },
        {
          kind: 'boolean',
          path: 'active',
          labelKey: 'builderScreen.panel.properties.active',
          level: 'advanced'
        },
        {
          kind: 'boolean',
          path: 'disabled',
          labelKey: 'builderScreen.inspector.disabled',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.inspector.groups.appearance.title',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'token',
          path: 'surface',
          target: 'style',
          tokenSet: 'surface',
          labelKey: 'builderScreen.panel.section.contentStyle',
          level: 'advanced'
        }
      ]
    }
  ]
});

// ---------------------------------------------------------------- linha

export const rowDescriptor = defineDescriptor<'row'>({
  type: 'row',
  labelKey: 'builderScreen.palette.row',
  descriptionKey: 'builderScreen.palette.row.desc',
  icon: 'align-left',
  category: 'layout',
  level: 'simple',

  createDefault: context => ({
    props: { gap: 'normal', alignItems: 'start', justifyContent: 'start' },
    layout: { order: context.order, colSpan: 24 },
    children: []
  }),

  container: {
    layoutMode: 'row',
    accepts: [...FIELD_TYPES]
  },

  // `tab` junto: a aba passou a aceitar conteudo direto, e uma linha e
  // conteudo — ver o comentario em `tabDescriptor`.
  allowedParents: ['panel', 'section', 'tab'],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.rowSection',
      level: 'simple',
      properties: [
        {
          kind: 'enum',
          path: 'gap',
          labelKey: 'builderScreen.inspector.fieldGap',
          level: 'simple',
          options: [
            { value: 'none', labelKey: 'builderScreen.common.none' },
            { value: 'small', labelKey: 'builderScreen.inspector.gap.small' },
            { value: 'medium', labelKey: 'builderScreen.inspector.gap.medium' },
            { value: 'normal', labelKey: 'builderScreen.inspector.gap.normal' },
            { value: 'large', labelKey: 'builderScreen.inspector.gap.large' },
            { value: 'extraLarge', labelKey: 'builderScreen.inspector.gap.extraLarge' }
          ]
        },
        {
          kind: 'enum',
          path: 'alignItems',
          labelKey: 'builderScreen.inspector.align.top',
          level: 'advanced',
          options: [
            { value: 'start', labelKey: 'builderScreen.inspector.align.top' },
            { value: 'center', labelKey: 'builderScreen.inspector.align.center' },
            { value: 'end', labelKey: 'builderScreen.inspector.align.bottom' },
            { value: 'stretch', labelKey: 'builderScreen.inspector.align.stretch' }
          ]
        },
        {
          kind: 'enum',
          path: 'justifyContent',
          labelKey: 'builderScreen.inspector.justify.start',
          level: 'advanced',
          options: [
            { value: 'start', labelKey: 'builderScreen.inspector.justify.start' },
            { value: 'center', labelKey: 'builderScreen.inspector.align.center' },
            { value: 'end', labelKey: 'builderScreen.inspector.justify.end' },
            { value: 'space-between', labelKey: 'builderScreen.inspector.justify.spaceBetween' },
            { value: 'space-around', labelKey: 'builderScreen.inspector.justify.spaceAround' }
          ]
        }
      ]
    }
  ]
});

// ---------------------------------------------------------------- seção

export const sectionDescriptor = defineDescriptor<'section'>({
  type: 'section',
  labelKey: 'builderScreen.palette.section',
  descriptionKey: 'builderScreen.palette.section.desc',
  icon: 'minus',
  category: 'layout',
  level: 'advanced',

  createDefault: context => ({
    props: { collapsible: false, collapsed: false },
    layout: { order: context.order, colSpan: 24 },
    children: []
  }),

  container: {
    layoutMode: 'grid',
    accepts: ['row', ...FIELD_TYPES]
  },

  allowedParents: ['panel', 'tab'],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'titleKey',
          labelKey: 'builderScreen.inspector.sectionTitle',
          helpKey: 'builderScreen.inspector.sectionTitle.help',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'collapsible',
          labelKey: 'builderScreen.inspector.collapsible',
          level: 'advanced'
        }
      ]
    }
  ]
});

export const CONTAINER_DESCRIPTORS: readonly ComponentDescriptor[] = [
  screenDescriptor,
  tabsDescriptor,
  tabDescriptor,
  panelDescriptor,
  rowDescriptor,
  sectionDescriptor
];
